import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useUser } from '@/hooks/users/useUser'
import { useOrders } from '@/hooks/commerce/useOrders'
import { hasPlans } from '@/constants/hasPlans'
import { isInRouteScope } from '@/utils/routes'
import {
  getSessionStorageData,
  setSessionStorageData,
  removeSessionStorageData
} from '@/utils/handleSessionStorage'
import type { ModalTrigger } from '../types'

/**
 * Visibility for a `plan-check` modal: the user is logged in, holds an active
 * order in `config.plans.inclusive` and none in `exclusive`, is within any
 * `routes` scope, and hasn't already seen it this session. Shown once per
 * session (`sessionStorage`); the gate is cleared on logout so a later
 * re-eligibility can show it again.
 */
export const usePlanCheckTrigger: ModalTrigger = (modal) => {
  // `useUser` (not `useUserIdentified`) — the trigger only needs `isLoggedIn`,
  // and one instance per modal must not re-fire identification/GTM side effects.
  const { isLoggedIn } = useUser()
  const { orders, isFetching } = useOrders({ enabled: isLoggedIn })
  const pathname = usePathname()

  const [isVisible, setIsVisible] = useState(false)

  const sessionKey = `modal:${modal._id}`
  const inRouteScope = isInRouteScope(modal.routes, modal.excludeRoutes, pathname)
  const plans = modal.type === 'plan-check' ? modal.config.plans : undefined

  useEffect(() => {
    if (!isLoggedIn) {
      removeSessionStorageData(sessionKey)
      return
    }

    if (!inRouteScope || getSessionStorageData(sessionKey) || isFetching || !orders) return

    if (hasPlans(orders, plans)) {
      // Defer the state update out of the effect body (avoids
      // react-hooks/set-state-in-effect) and mark seen for this session.
      requestAnimationFrame(() => {
        setIsVisible(true)
        setSessionStorageData(sessionKey, 'true')
      })
    }
  }, [isLoggedIn, inRouteScope, isFetching, orders, plans, sessionKey])

  const dismiss = () => setIsVisible(false)

  return { isVisible, dismiss }
}
